import { useCallback, useSyncExternalStore } from 'react';

/** Tailwind's `sm` breakpoint (px); below it is treated as mobile. */
export const MOBILE_BREAKPOINT = 640;

const NOOP = () => {
  // no-op teardown when matchMedia is unavailable (SSR)
};

// Cache one MediaQueryList per query so getSnapshot doesn't re-allocate each render.
const listCache = new Map<string, MediaQueryList>();

function getMediaQueryList(query: string): MediaQueryList | null {
  if (typeof window === 'undefined' || !window.matchMedia) {
    return null;
  }
  let list = listCache.get(query);
  if (!list) {
    list = window.matchMedia(query);
    listCache.set(query, list);
  }
  return list;
}

/** Reactively tracks a CSS media query; falls back to `serverFallback` without `matchMedia` (SSR). */
export function useMediaQuery(query: string, serverFallback = false): boolean {
  const subscribe = useCallback(
    (onChange: () => void) => {
      const list = getMediaQueryList(query);
      if (!list) {
        return NOOP;
      }
      list.addEventListener('change', onChange);
      return () => list.removeEventListener('change', onChange);
    },
    [query],
  );

  return useSyncExternalStore(
    subscribe,
    () => getMediaQueryList(query)?.matches ?? serverFallback,
    () => serverFallback,
  );
}

/** True below the `sm` breakpoint. Assumes desktop (`false`) during SSR. */
export function useIsMobile(): boolean {
  return useMediaQuery(`(max-width: ${MOBILE_BREAKPOINT - 1}px)`);
}
